import React, { useState, useEffect, JSX } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import clsx from 'clsx';
import {
  HiOutlineArrowLeft,
  HiExclamationCircle,
  HiCog,
  HiAnnotation,
  HiLightBulb,
  HiChevronDown,
  HiChevronUp,
  HiCheckCircle
} from 'react-icons/hi';
import Gauge from './Gauge'; 

import 'dayjs/locale/fa';
import './PlanDetailsPage.scss';
import logoImage_1 from '../../assets/White PNG 1.png';
import logoImage_2 from '../../assets/Black PNG 1.png';
import { TextField } from '@mui/material';
import { LocalizationProvider, faIR, MobileTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { format, parse } from 'date-fns';

interface PlanOptionField {
  name: string;
  type: string;
  order?: number;
  label?: string;
  title?: string;
  description?: string;
  note?: string;
  default?: any;
  defaults?: any[];
  options?: { value: string; label: string }[] | string[];
  properties?: { [key: string]: PlanOptionField };
  items?: PlanOptionField;
  data?: (string | { id: string; type: string; minimum?: number; maximum?: number; default?: any; options?: string[]; place_holder?: string; required?: boolean; })[];
  min_length?: number;
  max_length?: number;
  minimum?: number;
  maximum?: number;
  place_holder?: string;
  editable?: boolean;
  required?: boolean;
  is_required?: boolean;
  validation?: {
    danger?: [number, number];
    warning?: [number, number];
  }
}

const isFieldRequired = (field?: PlanOptionField | null) => field?.is_required ?? field?.required ?? true;

const isEmptyFieldValue = (value: any) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '') ||
  (Array.isArray(value) && value.length === 0);

interface PlanOptions {
  plan_option_id: number;
  settings: {
    [key: string]: PlanOptionField;
  };
}

const PlanSettingsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);

  const theme = queryParams.get('theme') || 'dark';
  const planId = parseInt(queryParams.get('planId') || '0');
  const accountId = queryParams.get('accountId');
  const planName = queryParams.get('planName') || 'نامشخص';
  const accountNumberFromQuery = queryParams.get('accountNumber') ? decodeURIComponent(queryParams.get('accountNumber') as string) : 'نامشخص';
  const accountServerFromQuery = queryParams.get('accountServer') ? decodeURIComponent(queryParams.get('accountServer') as string) : 'نامشخص';

  const [planSettingsFormData, setPlanSettingsFormData] = useState<any>({});
  const [planOptions, setPlanOptions] = useState<PlanOptions | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
  const [isSubmittingSettings, setIsSubmittingSettings] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  const toggleDescriptionExpansion = (key: string) => {
    setExpandedDescriptions(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const getSeverityClass = (field: PlanOptionField, value: number | string): string => {
    if (value === '' || value === null || value === undefined) {
        return '';
    }
    const numericValue = Number(value);
    if (isNaN(numericValue)) {
        return '';
    }

    if (field.validation!==null){ 
      if (field.validation.danger!==null){ 
        if (numericValue >= field.validation.danger[0] && numericValue <= field.validation.danger[1]) 
          return 'input-danger'; 
      }
      else if (field.validation.warning!==null){ 
        if (numericValue >= field.validation.warning[0] && numericValue <= field.validation.warning[1]) 
          return 'input-warning'; 
      }
      else {
        return 'input-safe'; 
      }
    }
    return '';
  };


  useEffect(() => {
    const fetchPlanOptions = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('توکن احراز هویت یافت نشد. لطفاً ابتدا وارد شوید.');

        const plans_response = await fetch(`https://api.neontradepro.com/plan`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!plans_response.ok) throw new Error('خطا در دریافت لیست پلن ها.');

        const plans = await plans_response.json();
        const planData = plans.find((plan: any) => plan.id === planId);
        if (!planData) throw new Error('اطلاعات پلن یافت نشد.');

        const response = await fetch(`https://api.neontradepro.com/plan/options/${planData.plan_option_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('خطا در دریافت تنظیمات پلن.');

        const rawData = await response.json();
        const settingsData: PlanOptions = {
          plan_option_id: planData.plan_option_id,
          settings: rawData.data || {}
        };
        setPlanOptions(settingsData);
        
        const initialPlanSettings: { [key: string]: any } = {};
        for (const key in settingsData.settings) {
            const field = settingsData.settings[key];
            if (field.editable === false) continue;
            if (field.type === 'object') {
              initialPlanSettings[key] = {};
              if (field.properties) {
                for (const propKey in field.properties) {
                    initialPlanSettings[key][propKey] = field.properties[propKey].default || '';
                }
              }
            } else if (field.type === 'array' || field.type === 'checkbox') {
              initialPlanSettings[key] = field.defaults || field.default || [];
            } else if (field.type === 'combine') {
                initialPlanSettings[key] = {};
                field.data?.forEach(item => {
                    if (typeof item !== 'string' && item.id) {
                      initialPlanSettings[key][item.id] = item.default !== undefined ? item.default : '';
                    }
                });
            } else {
              initialPlanSettings[key] = field.default !== undefined ? field.default : '';
            }
        }
        setPlanSettingsFormData(initialPlanSettings);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (planId && accountId) {
      fetchPlanOptions();
    } else {
      setError('شناسه پلن یا حساب نامعتبر است.');
      setLoading(false);
    }
  }, [planId, accountId]);
  
  const handlePlanSettingChange = (key: string, value: any, parentKey?: string) => {
    setPlanSettingsFormData((prev: any) => {
      const newPlanSettings = { ...prev };
      if (parentKey) {
        newPlanSettings[parentKey] = { ...newPlanSettings[parentKey], [key]: value };
      } else {
        newPlanSettings[key] = value;
      }
      return newPlanSettings;
    });
  };

  const handleArraySettingChange = (key: string, itemValue: string) => {
    setPlanSettingsFormData((prev: any) => {
      const currentArray = prev[key] || [];
      const newArray = currentArray.includes(itemValue)
        ? currentArray.filter((item: string) => item !== itemValue)
        : [...currentArray, itemValue];
      return { ...prev, [key]: newArray };
    });
  };

  const handleSubmitSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingSettings(true);
    setError(null);
    setSuccess(null);
    setValidationErrors({});

        const newErrors: { [key: string]: string } = {};
    if (planOptions?.settings) {
      for (const key in planOptions.settings) {
        const field = planOptions.settings[key];
        if (field.editable === false) continue;

        const isRequired = isFieldRequired(field);

        if (isRequired) {
          const value = planSettingsFormData[key];
          if (field.type === 'object' && field.properties) {
            for (const propKey in field.properties) {
              const propField = field.properties[propKey];
              const propValue = value?.[propKey];
              if (isFieldRequired(propField) && isEmptyFieldValue(propValue)) {
                newErrors[`${key}.${propKey}`] = `فیلد "${propField.label || propField.title || propKey}" اجباری است.`;
              }
            }
          } else if (field.type === 'combine' && field.data) {
             field.data.forEach(item => {
                if (typeof item !== 'string') {
                    const combineValue = value?.[item.id];
                    if ((item.required ?? true) && isEmptyFieldValue(combineValue)) {
                        newErrors[`${key}.${item.id}`] = `فیلد "${item.id}" اجباری است.`;
                    }
                }
             });
          } else {
            const isValueEmpty = isEmptyFieldValue(value);
            if (isValueEmpty) {
              newErrors[key] = `فیلد "${field.label || field.title || key}" اجباری است.`;
            }
          }
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setValidationErrors(newErrors);
      setIsSubmittingSettings(false);
      setError('لطفاً تمام فیلدهای اجباری را تکمیل کنید.');
      return;
    }

    if (!accountId) {
      setError('شناسه حساب یافت نشد.');
      setIsSubmittingSettings(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('توکن احراز هویت یافت نشد.');
      setIsSubmittingSettings(false);
      return;
    }

    try {
      const response = await fetch(`https://api.neontradepro.com/trade-account/${accountId}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan_settings: planSettingsFormData }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        let errorMessage = 'خطا در ثبت تنظیمات.';
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map((err: any) => {
              const fieldName = err.loc[err.loc.length - 1];
              return `فیلد ${fieldName}: ${err.msg}`;
            }).join('\n');
          } else {
            errorMessage = errorData.detail;
          }
        }
        throw new Error(errorMessage);
      }

      setSuccess('تنظیمات با موفقیت برای حساب شما ثبت شد!');
      setTimeout(() => {
        navigate('/dashboard', { state: { tab: 1, refresh: true } });
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSubmittingSettings(false);
    }
  };

  const renderPlanSettingField = (fieldKey: string, field: PlanOptionField, parentKey?: string) => {
    const fullKey = parentKey ? `${parentKey}.${fieldKey}` : fieldKey;
    const error = validationErrors[fullKey];

    let fieldValue = parentKey
      ? (planSettingsFormData[parentKey]?.[fieldKey] ?? '')
      : (planSettingsFormData[fieldKey] ?? '');

    const displayLabel = field.label || field.title;
    let content;
    const fieldIsEditable = field.editable ?? true;
    const inputErrorClass = error ? 'input-error' : '';
    const required = isFieldRequired(field);
    const fieldIndicator = required
      ? <span style={{ color: 'red' }}>*</span>
      : <span style={{ color: 'var(--text-secondary, #9ca3af)', fontSize: '0.9rem' }}>(اختیاری)</span>;

    switch (field.type) {
      case 'string':
      case 'text':
        content = (
          <div className="input-group">
            <label htmlFor={fieldKey}>{displayLabel} {fieldIndicator}</label>
            <input type="text" id={fieldKey} value={fieldValue} onChange={(e) => handlePlanSettingChange(fieldKey, e.target.value, parentKey)} placeholder={field.place_holder || ''} disabled={!fieldIsEditable} className={inputErrorClass} />
            {error && <span className="field-error-message">{error}</span>}
          </div>
        );
        break;
      
      case 'textarea':
        content = (
          <div className="input-group">
            <label htmlFor={fieldKey}>{displayLabel} {fieldIndicator}</label>
            <textarea id={fieldKey} value={fieldValue} onChange={(e) => handlePlanSettingChange(fieldKey, e.target.value, parentKey)} placeholder={field.place_holder || ''} disabled={!fieldIsEditable} rows={5} className={inputErrorClass}></textarea>
            {error && <span className="field-error-message">{error}</span>}
          </div>
        );
        break;

      case 'radio':
        content = (
          <div className={`input-group radio-button-group ${inputErrorClass}`}>
            <label>{displayLabel} {fieldIndicator}</label>
            <div className="radio-options-wrapper">
              {Array.isArray(field.options) && (field.options as { value: string; label: string }[]).map(option => (
                <div key={option.value} className="radio-item">
                  <input
                    type="radio"
                    id={`${fullKey}-${option.value}`}
                    name={fullKey}
                    value={option.value}
                    checked={fieldValue === option.value}
                    disabled={!fieldIsEditable}
                    onChange={(e) => handlePlanSettingChange(fieldKey, e.target.value, parentKey)}
                  />
                  <label htmlFor={`${fullKey}-${option.value}`}>{option.label}</label>
                </div>
              ))}
            </div>
            {error && <span className="field-error-message">{error}</span>}
          </div>
        );
        break;

      case 'number':
      case 'int':
        const severityClass = getSeverityClass(field, fieldValue);
    
        const hasGauge = field.validation && (field.validation.danger || field.validation.warning); 
        
        const gaugeMin = field.minimum ?? 0; 
        const gaugeMax = field.maximum ?? 100; 

        const numericValue = Number(fieldValue);

        content = (
          <div className="input-group">
            <label htmlFor={fieldKey}>{displayLabel} {fieldIndicator}</label>
            
            {hasGauge && ( 
              <Gauge
                value={isNaN(numericValue) ? gaugeMin : numericValue} 
                min={gaugeMin} 
                max={gaugeMax} 
                danger={field.validation?.danger} 
                warning={field.validation?.warning} 
              />
            )}

            <input 
              type="number" 
              id={fieldKey} 
              value={fieldValue} 
              disabled={!fieldIsEditable} 
              onChange={(e) => handlePlanSettingChange(fieldKey, e.target.value === '' ? '' : parseFloat(e.target.value) || 0, parentKey)} 
              placeholder={field.place_holder || ''} 
              className={`${inputErrorClass} ${severityClass}`}
              min={field.minimum} 
              max={field.maximum} 
            />
            {error && <span className="field-error-message">{error}</span>}
          </div>
        );
        break;
      
      case 'array':
      case 'checkbox':
        content = (
          <div className={`input-group array-checkbox-group ${inputErrorClass}`}>
            <label>{displayLabel} {fieldIndicator}</label>
            {Array.isArray(field.options) && (field.options as string[]).map(option => (
              <div key={`${fullKey}-${option}`} className="checkbox-item">
                <input type="checkbox" id={`${fullKey}-${option}`} checked={Array.isArray(fieldValue) && fieldValue.includes(option)} disabled={!fieldIsEditable} onChange={() => handleArraySettingChange(fieldKey, option)} />
                <label htmlFor={`${fullKey}-${option}`}>{option}</label>
              </div>
            ))}
            {error && <span className="field-error-message">{error}</span>}
          </div>
        );
        break;
      
      case 'object':
        content = (
          <div className="input-group nested-object-group">
            {field.properties && Object.entries(field.properties)
              .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
              .map(([propKey, propValue]) => renderPlanSettingField(propKey, propValue, fieldKey))
            }
          </div>
        );
        break;

      case 'combine':
        let items: JSX.Element[] = [];
        let rows: JSX.Element[] = [];

        field.data?.forEach((item: any, index: number) => {
          if (typeof item === 'string') {
            rows.push(<span key={index}>{item}</span>);
            return;
          }
          const subFieldKey = item.id;
          const subFieldValue = planSettingsFormData[fieldKey]?.[subFieldKey] ?? (item.default ?? '');
          const combineFieldError = validationErrors[`${fieldKey}.${subFieldKey}`];
          const combineFieldRequired = item.required ?? true;
          const combinePlaceholder = item.place_holder || (combineFieldRequired ? '' : 'اختیاری');
          switch (item.type) {
            case 'string':
            case 'text':
              rows.push(
                <input
                  key={index}
                  className={clsx("combine-input", combineFieldError && "input-error")}
                  type="text"
                  value={subFieldValue}
                  onChange={(e) => handlePlanSettingChange(subFieldKey, e.target.value, fieldKey)}
                  placeholder={combinePlaceholder}
                />
              );
              return;
            case 'int':
              rows.push(<input key={index} className={clsx("combine-input", combineFieldError && "input-error")} type="number" value={subFieldValue} onChange={(e) => handlePlanSettingChange(subFieldKey, e.target.value === '' ? '' : parseInt(e.target.value, 10) || 0, fieldKey)} placeholder={combinePlaceholder} />);
              return;
            case 'select':
              rows.push(
                <select key={index} className={clsx("combine-input", combineFieldError && "input-error")} value={subFieldValue} onChange={(e) => handlePlanSettingChange(subFieldKey, e.target.value, fieldKey)}>
                  {!combineFieldRequired && <option value="">انتخاب اختیاری</option>}
                  {item.options?.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              );
              return;
            case 'textarea':
              if (rows.length > 0){
                items.push(<div key={`wrapper-${index}`} className="combine-inputs-wrapper">{rows}</div>);
                rows = [];
              }
              items.push(
                <div key={index}>
                  <textarea className={clsx("combine-input", combineFieldError && "input-error")} value={subFieldValue} onChange={(e) => handlePlanSettingChange(subFieldKey, e.target.value, fieldKey)} placeholder={combinePlaceholder} />
                  {combineFieldError && <span className="field-error-message">{combineFieldError}</span>}
                </div>
              );
              return;
            case 'time':
              const timeAsDate = fieldValue && fieldValue[item.id] ? parse(fieldValue[item.id], 'HH:mm', new Date()) : null;
              rows.push(
                  <LocalizationProvider
                    key={index}
                    dateAdapter={AdapterDayjs}
                    adapterLocale="fa"
                    localeText={{
                      ...faIR.components.MuiLocalizationProvider.defaultProps.localeText,
                      okButtonLabel: 'تایید',
                    }}
                  >
                    <MobileTimePicker
                      value={timeAsDate}
                      onChange={(newValue: Date | null) => {
                        if (newValue) {
                          try{
                            const formattedTime = format(newValue, 'HH:mm');
                            handlePlanSettingChange(fieldKey, {...fieldValue, [item.id]: formattedTime}, parentKey);
                          }
                          catch(e){}
                        }
                      }}
                      renderInput={(params) => (
                        <TextField 
                          {...params}
                          className="time-picker-input"
                          error={Boolean(combineFieldError)}
                          helperText={combineFieldError || (!combineFieldRequired ? 'اختیاری' : '')}
                          InputProps={{ ...params.InputProps, readOnly: true }}
                        />
                      )}
                      DialogProps={{
                        PaperProps: {
                          className: `time-picker-dialog ${theme}`
                        }
                      }}
                    />
                  </LocalizationProvider>
              );
              return;
            default: return null;
          }
        });
        
        if (rows.length > 0) {
          items.push(<div key={fieldKey} className="combine-inputs-wrapper">{rows}</div>);
        }

        content = (
          <div className={`input-group combine-group ${inputErrorClass}`}>
            <label>{displayLabel} {fieldIndicator}</label>
            {items}
            {!required && <span className="field-error-message" style={{ width: '100%', marginTop: '0.5rem', color: 'var(--text-secondary, #9ca3af)' }}>پر کردن این بخش الزامی نیست.</span>}
          </div>
        );
        break;

      default:
        content = null;
    }
    
    if (!content) return null;

    if (field.type === 'object') {
      return (
        <div key={fullKey} className="setting-card">
          <div className="setting-card-header" onClick={() => toggleDescriptionExpansion(fieldKey)}>
            <h3><HiCog className="header-icon" /> {displayLabel}</h3>
            {expandedDescriptions[fieldKey] ? <HiChevronUp /> : <HiChevronDown />}
          </div>
          {expandedDescriptions[fieldKey] && (
            <div className="setting-card-content">
              {field.description && <div className="field-description"><ReactMarkdown>{field.description}</ReactMarkdown></div>}
              {content}
              {field.note && <p className="field-note"><HiLightBulb /> <ReactMarkdown components={{ p: 'span' }}>{field.note}</ReactMarkdown></p>}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={fullKey} className="setting-card">
        <div className="setting-card-header" onClick={() => field.description && toggleDescriptionExpansion(fieldKey)}>
          <h3><HiAnnotation className="header-icon" /> {displayLabel}</h3>
          {field.description && (expandedDescriptions[fieldKey] ? <HiChevronUp /> : <HiChevronDown />)}
        </div>
        {field.description && expandedDescriptions[fieldKey] && (
          <div className="setting-card-content">
            <div className="field-description"><ReactMarkdown>{field.description}</ReactMarkdown></div>
          </div>
        )}
        <div className="setting-card-content">
          {content}
          {field.note && <p className="field-note"><HiLightBulb /> <ReactMarkdown components={{ p: 'span' }}>{field.note}</ReactMarkdown></p>}
        </div>
      </div>
    );
  };
  
  const pageHeaderStyle: React.CSSProperties = {
    padding: '1rem 1.5rem', borderBottom: '1px solid var(--border-color, #333)',
    display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1000,
    backgroundColor: theme === 'dark' ? '#121212' : '#f0f0f0',
  };

  const handleBack = () => {
    const params = new URLSearchParams();
    params.set('theme', theme);
    if (planId) params.set('planId', planId.toString());
    if (planName) params.set('planName', encodeURIComponent(planName));
    if (accountId) params.set('accountId', accountId);
    if (accountNumberFromQuery) params.set('accountNumber', encodeURIComponent(accountNumberFromQuery));
    if (accountServerFromQuery) params.set('accountServer', encodeURIComponent(accountServerFromQuery));
    navigate(`/confirm-purchase?${params.toString()}`)
  }

  if (loading) {
    return (
      <div className={`plan-details-page ${theme}`} style={{textAlign: 'center', padding: '2rem'}}>
        <h2>در حال بارگذاری تنظیمات پلن...</h2>
      </div>
    );
  }

  if (error && !Object.keys(validationErrors).length) {
    return (
      <div className={`plan-details-page ${theme}`} style={{textAlign: 'center', padding: '2rem'}}>
        <HiExclamationCircle style={{ fontSize: '3rem', color: '#dc3545' }} />
        <h2 style={{ color: '#dc3545' }}>خطا در بارگذاری</h2>
        <p>{error}</p>
      </div>
    );
  }
  
  return (
    <>
      <header style={pageHeaderStyle}>
        <button onClick={handleBack} className='details-back-link' style={{ marginRight: '1rem' }}>
          <HiOutlineArrowLeft /> بازگشت
        </button>
        <div className='logo'><Link to={`/?theme=${theme}`}><img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt='لوگوی نئون' style={{ height: '35px' }} /></Link></div>
      </header>
      <div className={`plan-details-page ${theme}`}>
        <div className="details-container">
          <div className="details-header" style={{ marginBottom: '1.5rem' }}>
            <h1>تنظیمات پلن "{planName}"</h1>
          </div>
          
          <div className="plan-summary" style={{ marginBottom: '2rem', padding: '1rem', background: 'var(--pdp-feature-card-bg)', borderRadius: '8px', border: '1px solid var(--pdp-feature-card-border)' }}>
            <p style={{ fontSize: '1.1rem', fontWeight: '600' }}>
              حساب انتخابی: <strong>{accountNumberFromQuery}</strong>
              {accountServerFromQuery !== 'نامشخص' && ` (سرور: ${accountServerFromQuery})`}
            </p>
          </div>

          <form onSubmit={handleSubmitSettings} className="plan-settings-form" noValidate>
            <h3 className="section-title"><HiCog /> پارامترهای قابل تنظیم</h3>
            {planOptions && planOptions.settings && (
              <div className="plan-settings-section">
                {Object.entries(planOptions.settings)
                  .sort(([, a], [, b]) => (a.order || 0) - (b.order || 0))
                  .map(([key, field]) => renderPlanSettingField(key, field))
                }
              </div>
            )}
            <button type="submit" className="select-plan-btn-details" disabled={isSubmittingSettings} style={{ background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))', width:'60%', margin: '2rem auto', padding:'0.75rem 0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
             {isSubmittingSettings ? 'در حال ثبت تنظیمات...' : 'ثبت تنظیمات نهایی'}
            </button>
          </form>
          {(success || error) && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001 }}>
              <div style={{ background: theme === 'dark' ? '#2c2c2c' : '#ffffff', padding: '2rem', borderRadius: '10px', maxWidth: '400px', width: '90%', textAlign: 'center', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', color: theme === 'dark' ? '#fff' : '#333', direction: 'rtl' }}>
                {error && <HiExclamationCircle style={{ fontSize: '3rem', color: '#ffc107', marginBottom: '1rem' }} />}
                {success && <HiCheckCircle style={{ fontSize: '3rem', color: '#28a745', marginBottom: '1rem' }} />}
                <h3 style={{ marginBottom: '1rem', color: error ? '#ffc107' : '#28a745' }}>{error ? 'خطا!' : 'موفقیت!'}</h3>
                <p style={{ marginBottom: '1.5rem', whiteSpace: 'pre-wrap' }}>{error || success}</p>
                <button onClick={() => { setError(null); setSuccess(null); if (success) navigate('/dashboard', { state: { tab: 1 } }); }} style={{ padding: '0.75rem 2rem', background: 'var(--primary-color)', color: 'var(--button-primary-text)', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 'bold' }}>
                  متوجه شدم
                </button>
              </div>
            </div>
          )}
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to={`/dashboard`} className="details-back-link">
              <HiOutlineArrowLeft /> بازگشت به پنل کاربری
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PlanSettingsPage;
